"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader as Loader2 } from 'lucide-react';
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="17869" className="w-full h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8 border border-slate-200 bg-white/90 backdrop-blur-sm rounded-md animate-in fade-in duration-500">
        <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Request Received</h3>
        <p className="text-slate-600 font-sans">{successMessage}</p>
      </div>;
  }
  const inputClasses = "border border-slate-300 rounded-lg bg-white px-4 py-2 text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:border-[#4F46E5] transition-all duration-300 shadow-sm";
  const labelClasses = "uppercase tracking-[0.15em] font-semibold text-slate-600 text-xs font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="JANE DOE" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="JANE@EXAMPLE.COM" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 303 555 0123" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="relationship" rules={{
        required: "Relationship is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>I am a…</FormLabel>
              <FormControl>
                <select {...field} className={`${inputClasses} w-full cursor-pointer`}>
                  <option value="Patient">Patient</option>
                  <option value="Family Member / Caregiver">Family Member / Caregiver</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="support_needed" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>How can I help?</FormLabel>
            <FormControl>
              <Textarea placeholder="TELL US MORE ABOUT YOUR NEEDS" {...field} className={`${inputClasses} min-h-[120px] resize-none`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[220px] h-14 bg-[#4F46E5] text-white hover:bg-[#4338CA] transition-all duration-300 uppercase tracking-widest text-sm font-bold rounded-full shadow-lg shadow-indigo-500/20 border-none">
          {isSubmitting ? <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </> : "Get Support Now"}
        </Button>
      </div>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-personal-support" ref={sectionRef} className="relative w-full py-[160px] bg-[#0B1528] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.12] pointer-events-none z-0 mix-blend-overlay" style={{
      backgroundImage: `linear-gradient(to bottom, rgba(11, 21, 40, 0.9), rgba(11, 21, 40, 0.7)), url('https://images.unsplash.com/photo-1549400299-f99728cb52a0?q=80&w=2070&auto=format&fit=crop')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} />

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          <div className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-5xl md:text-[48px] font-serif font-normal text-[#F8FAFC] mb-8 tracking-tight leading-[1.1]">
              STILL HAVE<br />QUESTIONS?
            </h2>
            <div className="w-16 h-1.5 bg-[#4F46E5] mb-8 rounded-full" />
            <p className="text-slate-300 font-sans text-lg leading-relaxed max-w-md">
              I'm here to guide you through every step of your journey. Whether you're seeking clarity on navigation services or need direct family support, our team is ready to provide the personal attention you deserve.
            </p>
            
            <div className="mt-12 space-y-6">
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-slate-400 mb-1 font-sans font-semibold">MEET IN PERSON&nbsp;</span>
                <span className="text-[#F8FAFC] font-sans text-lg">Denver, CO</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-slate-400 mb-1 font-sans font-semibold">Direct Support</span>
                <span className="text-[#F8FAFC] font-sans text-lg">info@myprostatepath.com</span>
              </div>
            </div>
          </div>

          <div className={`lg:col-span-7 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-[#F1F5F9] p-8 md:p-12 rounded-md shadow-2xl">
              <FormProvider formKey="personal_support_form_faq" formId="personal_support_form_faq" sectionName="get-personal-support" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              relationship: "Patient",
              support_needed: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              relationship: {
                required: true
              },
              support_needed: {
                required: true
              }
            }} submitText="Get Support Now" successMessage="Your request has been received. A support specialist will contact you within 24 hours.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}